// N$ / € formatting for the bookings screens (D7: NAD renders always, EUR
// only when the row actually carries a fee_eur_cents value — the live flow
// charges NAD as ZAR at the 1:1 peg, and fee_eur_cents/fx_rate_nad_per_eur
// land nullable and unwritten per D7, so most rows never show a € figure).
//
// Hand-rolled rather than `Intl.NumberFormat('en-NA', ...)`: Namibia's `en-NA`
// locale data is inconsistently shipped across browsers/Node ICU builds, and
// the desired format ("N$1,234.50") is simpler to guarantee directly than to
// coax out of a currency formatter with a non-ISO symbol.

/** Formats a NAD amount (number, numeric string, or null/undefined) as
 * "N$1,234.50". Never negative in the output — callers that need a signed
 * ledger entry (e.g. an "undo" row) format the sign themselves. */
export function formatNad(amount: number | string | null | undefined): string {
  const n = toNumber(amount);
  const safe = Number.isFinite(n) ? Math.abs(n) : 0;
  return `N$${groupThousands(safe.toFixed(2))}`;
}

/** Signed variant of `formatNad` — a leading `-` when negative, otherwise
 * identical (Task 24 review round 2, item 2). For a balance-by-method net:
 * a `balance.undone` whose matching `balance.recorded` fell in an EARLIER
 * statement period (the undo window is ≤24h, but a record right at a
 * month boundary can straddle two periods) sums to a genuine negative net
 * for that one method in the LATER period — `formatNad`'s `Math.abs` would
 * silently print that as a positive number, the opposite of what
 * happened. */
export function formatNadSigned(amount: number | string | null | undefined): string {
  const n = toNumber(amount);
  return n < 0 ? `-${formatNad(n)}` : formatNad(n);
}

/** Formats a euro amount given in CENTS (bookings.fee_eur_cents is an
 * integer-cents column) as "€12.34". */
export function formatEurCents(cents: number | null | undefined): string {
  const n = typeof cents === 'number' && Number.isFinite(cents) ? Math.abs(cents) : 0;
  return `€${groupThousands((n / 100).toFixed(2))}`;
}

function toNumber(amount: number | string | null | undefined): number {
  if (amount === null || amount === undefined) return 0;
  return typeof amount === 'string' ? Number.parseFloat(amount) : amount;
}

function groupThousands(fixed: string): string {
  const [whole, frac] = fixed.split('.');
  const withCommas = whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${withCommas}.${frac}`;
}
